// Outbox Service for Offline Sales Queue Management
// Uniqueness is (sale_id, store_db). Public sale_id stays offline-{pc}-{n}.
import { getNextInvoiceNumber } from './invoiceNumberService';
import { getStorage } from './storageAdapter';
import StockRepository from './StockRepository';
import { withDbLock } from './dbLock';

const INSERT_OUTBOX_SALE_SQL = `INSERT INTO outbox_sales
                                (sale_id, date, status, upload_response, upload_date, payload, upload_invoice, attempts, last_attempt_date, store_db, client_meta)
                                VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`;

const toPositiveNumber = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return 0;
    return parsed;
};

const normalizeStoreDb = (storeDB) => String(storeDB || '').trim();

const extractStockDeltasFromPayload = (payload) => {
    const lines = Array.isArray(payload?.items) ? payload.items : [];
    const byPlu = new Map();

    for (const line of lines) {
        const plu = String(line?.Plu ?? line?.plu ?? line?.PLU ?? '').trim();
        if (!plu) continue;

        const qtyDelta = toPositiveNumber(line?.Qty ?? line?.qty ?? line?.QTY ?? line?.Quantity ?? line?.quantity);
        if (qtyDelta <= 0) continue;

        const key = plu.toLowerCase();
        const prev = byPlu.get(key);
        if (prev) {
            prev.qtyDelta = Number(prev.qtyDelta) + qtyDelta;
        } else {
            byPlu.set(key, { plu, qtyDelta });
        }
    }

    return Array.from(byPlu.values());
};

const withTransaction = async (storage, callback) => {
    if (storage?.platform === 'indexeddb') {
        return callback();
    }

    return withDbLock(async () => {
        let ownsTransaction = false;
        try {
            await storage.run('BEGIN IMMEDIATE TRANSACTION', []);
            ownsTransaction = true;
        } catch (beginError) {
            const msg = String(beginError?.message || '').toLowerCase();
            if (!msg.includes('within a transaction')) {
                throw beginError;
            }
        }

        try {
            const result = await callback();
            if (ownsTransaction) {
                await storage.run('COMMIT', []);
            }
            return result;
        } catch (error) {
            if (ownsTransaction) {
                try {
                    await storage.run('ROLLBACK', []);
                } catch (_) {
                    // Ignore rollback errors so original exception is preserved.
                }
            }
            throw error;
        }
    });
};

/**
 * Map database row to outbox item object
 * @param {Object} row Database row
 * @returns {Object} Outbox item
 */
function mapRowToItem(row) {
    return {
        saleId: row.sale_id,
        date: row.date,
        status: row.status,
        uploadResponse: row.upload_response ? JSON.parse(row.upload_response) : null,
        uploadDate: row.upload_date,
        payload: JSON.parse(row.payload),
        uploadInvoice: row.upload_invoice,
        attempts: row.attempts || 0,
        lastAttemptDate: row.last_attempt_date,
        storeDB: row.store_db,
        clientMeta: row.client_meta ? JSON.parse(row.client_meta) : {}
    };
}

/**
 * Enqueue a sale for offline synchronization
 * @param {Object} payload - Sale data to be synced
 * @param {String} storeDB - Store database identifier
 * @param {Object} clientMeta - Client metadata (cashier, appVersion, etc.)
 * @returns {Promise<String>} - Returns the saleId of the enqueued sale
 */
export async function enqueueSale(payload, storeDB, clientMeta = {}) {
    try {
        const storage = await getStorage();
        const storeKey = normalizeStoreDb(storeDB);
        const clientMetaWithTimestamp = {
            ...clientMeta,
            clientTimestamp: new Date().toISOString()
        };
        const nowIso = new Date().toISOString();

        const isUniqueConstraint = (error) =>
            String(error?.message || '').toLowerCase().includes('unique constraint failed');

        for (let attempt = 0; attempt < 5; attempt++) {
            const saleId = await getNextInvoiceNumber(storeKey);

            try {
                await storage.run(
                    INSERT_OUTBOX_SALE_SQL,
                    [
                        saleId,
                        nowIso,
                        'pending',
                        null,
                        null,
                        JSON.stringify(payload),
                        null,
                        0,
                        null,
                        storeKey,
                        JSON.stringify(clientMetaWithTimestamp)
                    ]
                );

                console.log(`[Outbox] Enqueued sale: ${saleId} store=${storeKey}`);
                try {
                    if (typeof storage.scheduleLytsoftBackup === 'function') {
                        storage.scheduleLytsoftBackup('outbox');
                    }
                } catch (_) {}
                return saleId;
            } catch (insertErr) {
                if (!isUniqueConstraint(insertErr) || attempt === 4) {
                    throw insertErr;
                }
                console.warn(`[Outbox] sale_id collision on attempt ${attempt + 1} (${saleId}, ${storeKey}), retrying...`);
            }
        }

        return null;
    } catch (error) {
        console.error('[Outbox] Failed to enqueue sale:', error);
        return null;
    }
}

/**
 * Enqueue a sale and atomically apply local stock decrement + dirty tracking.
 * @returns {Promise<{saleId: (string|null), stockUpdates: Array, stockDeltas: Array, localUpdatedAt: string}>}
 */
export async function enqueueSaleWithStockMutation(payload, storeDB, clientMeta = {}) {
    try {
        const storage = await getStorage();
        const storeKey = normalizeStoreDb(storeDB);
        const clientMetaWithTimestamp = {
            ...clientMeta,
            clientTimestamp: new Date().toISOString()
        };
        const localUpdatedAt = new Date().toISOString();
        const stockDeltas = extractStockDeltasFromPayload(payload);

        const isUniqueConstraint = (error) =>
            String(error?.message || '').toLowerCase().includes('unique constraint failed');

        for (let attempt = 0; attempt < 5; attempt++) {
            const saleId = await getNextInvoiceNumber(storeKey);

            try {
                const transactionResult = await withTransaction(storage, async () => {
                    await storage.run(
                        INSERT_OUTBOX_SALE_SQL,
                        [
                            saleId,
                            localUpdatedAt,
                            'pending',
                            null,
                            null,
                            JSON.stringify(payload),
                            null,
                            0,
                            null,
                            storeKey,
                            JSON.stringify(clientMetaWithTimestamp)
                        ]
                    );

                    let stockUpdates = [];
                    if (stockDeltas.length > 0) {
                        stockUpdates = await StockRepository.applyLocalStockDeltas(stockDeltas, {
                            localUpdatedAt,
                            storage,
                            useTransaction: false
                        });
                        await StockRepository.markDirtyPending(stockDeltas, saleId, {
                            localUpdatedAt,
                            storage,
                            useTransaction: false
                        });
                    }

                    return { saleId, stockUpdates };
                });

                console.log(`[Outbox] Enqueued sale with local stock mutation: ${transactionResult.saleId} store=${storeKey}`);
                try {
                    if (typeof storage.scheduleLytsoftBackup === 'function') {
                        storage.scheduleLytsoftBackup('outbox');
                    }
                } catch (_) {}
                return {
                    saleId: transactionResult.saleId,
                    stockUpdates: transactionResult.stockUpdates || [],
                    stockDeltas,
                    localUpdatedAt
                };
            } catch (insertErr) {
                if (!isUniqueConstraint(insertErr) || attempt === 4) {
                    throw insertErr;
                }
                console.warn(`[Outbox] sale_id collision on attempt ${attempt + 1} (${saleId}, ${storeKey}), retrying with stock mutation...`);
            }
        }

        return { saleId: null, stockUpdates: [], stockDeltas: [], localUpdatedAt };
    } catch (error) {
        console.error('[Outbox] Failed to enqueue sale with stock mutation:', error);
        return { saleId: null, stockUpdates: [], stockDeltas: [], localUpdatedAt: new Date().toISOString() };
    }
}

/**
 * @param {Number} batchSize
 * @returns {Promise<Array>}
 */
export async function getPending(batchSize = 10) {
    try {
        const storage = await getStorage();
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
        const rows = await storage.query(
            `SELECT * FROM outbox_sales
             WHERE status IN ('pending', 'timeout')
                OR (status = 'sending' AND last_attempt_date < ?)
             ORDER BY date ASC
                 LIMIT ?`,
            [fiveMinutesAgo, batchSize]
        );
        return rows.map(mapRowToItem);
    } catch (error) {
        console.error('[Outbox] Failed to get pending sales:', error);
        return [];
    }
}

/**
 * @param {String} saleId
 * @param {String} [storeDb]
 */
export async function markSending(saleId, storeDb) {
    try {
        const storage = await getStorage();
        const db = normalizeStoreDb(storeDb);
        if (db) {
            await storage.run(
                `UPDATE outbox_sales
                 SET status = 'sending',
                     attempts = attempts + 1,
                     last_attempt_date = ?,
                     updated_at = CURRENT_TIMESTAMP
                 WHERE sale_id = ? AND store_db = ?`,
                [new Date().toISOString(), saleId, db]
            );
        } else {
            await storage.run(
                `UPDATE outbox_sales
                 SET status = 'sending',
                     attempts = attempts + 1,
                     last_attempt_date = ?,
                     updated_at = CURRENT_TIMESTAMP
                 WHERE sale_id = ?`,
                [new Date().toISOString(), saleId]
            );
        }
    } catch (error) {
        console.error(`[Outbox] Failed to mark ${saleId} as sending:`, error);
    }
}

/**
 * @param {String} saleId
 * @param {Object} serverMeta
 * @param {String} [storeDb]
 */
export async function markSent(saleId, serverMeta = {}, storeDb) {
    try {
        const storage = await getStorage();
        const db = normalizeStoreDb(storeDb);
        const params = [
            JSON.stringify(serverMeta),
            new Date().toISOString(),
            serverMeta.invoiceNo || serverMeta.invoice || null,
            saleId
        ];
        if (db) {
            await storage.run(
                `UPDATE outbox_sales
                 SET status = 'sent',
                     upload_response = ?,
                     upload_date = ?,
                     upload_invoice = ?,
                     updated_at = CURRENT_TIMESTAMP
                 WHERE sale_id = ? AND store_db = ?`,
                [...params, db]
            );
        } else {
            await storage.run(
                `UPDATE outbox_sales
                 SET status = 'sent',
                     upload_response = ?,
                     upload_date = ?,
                     upload_invoice = ?,
                     updated_at = CURRENT_TIMESTAMP
                 WHERE sale_id = ?`,
                params
            );
        }
        console.log(`[Outbox] Marked ${saleId} as sent. Invoice: ${serverMeta.invoiceNo || serverMeta.invoice}`);
    } catch (error) {
        console.error(`[Outbox] Failed to mark ${saleId} as sent:`, error);
    }
}

/**
 * @deprecated
 */
export async function regenerateLocalId(saleId) {
    console.error(`[Outbox] regenerateLocalId blocked for ${saleId} — localId must stay stable for idempotency. Use Force Retry if required.`);
    throw new Error('localId must not be regenerated on normal sync. Use Force Retry for conflicts.');
}

export async function markFailed(saleId, reason, permanent = false, storeDb) {
    try {
        const storage = await getStorage();
        const db = normalizeStoreDb(storeDb);

        const item = db
            ? await storage.get(
                `SELECT attempts FROM outbox_sales WHERE sale_id = ? AND store_db = ?`,
                [saleId, db]
            )
            : await storage.get(
                `SELECT attempts FROM outbox_sales WHERE sale_id = ?`,
                [saleId]
            );

        if (item) {
            const maxAttempts = 5;
            const status = permanent || item.attempts >= maxAttempts ? 'failed' : 'timeout';
            const args = [
                status,
                JSON.stringify({
                    error: typeof reason === 'string' ? reason : JSON.stringify(reason),
                    failedAt: new Date().toISOString()
                }),
                new Date().toISOString(),
                saleId
            ];

            if (db) {
                await storage.run(
                    `UPDATE outbox_sales
                     SET status = ?,
                         upload_response = ?,
                         last_attempt_date = ?,
                         updated_at = CURRENT_TIMESTAMP
                     WHERE sale_id = ? AND store_db = ?`,
                    [...args, db]
                );
            } else {
                await storage.run(
                    `UPDATE outbox_sales
                     SET status = ?,
                         upload_response = ?,
                         last_attempt_date = ?,
                         updated_at = CURRENT_TIMESTAMP
                     WHERE sale_id = ?`,
                    args
                );
            }

            console.log(`[Outbox] Marked ${saleId} as ${status} (attempt ${item.attempts}${permanent ? ', permanent failure' : ''})`);
        }
    } catch (error) {
        console.error(`[Outbox] Failed to mark ${saleId} as failed:`, error);
    }
}

/**
 * @param {String} saleId
 * @param {String} [storeDb]
 */
export async function incrementRetries(saleId, storeDb) {
    try {
        const storage = await getStorage();
        const db = normalizeStoreDb(storeDb);
        if (db) {
            await storage.run(
                `UPDATE outbox_sales
                 SET attempts = attempts + 1,
                     status = 'pending',
                     last_attempt_date = ?,
                     updated_at = CURRENT_TIMESTAMP
                 WHERE sale_id = ? AND store_db = ?`,
                [new Date().toISOString(), saleId, db]
            );
        } else {
            await storage.run(
                `UPDATE outbox_sales
                 SET attempts = attempts + 1,
                     status = 'pending',
                     last_attempt_date = ?,
                     updated_at = CURRENT_TIMESTAMP
                 WHERE sale_id = ?`,
                [new Date().toISOString(), saleId]
            );
        }
        console.log(`[Outbox] Retry ${saleId}`);
    } catch (error) {
        console.error(`[Outbox] Failed to increment retries for ${saleId}:`, error);
    }
}

export async function countByStatus(status) {
    try {
        const storage = await getStorage();
        const result = await storage.get(
            `SELECT COUNT(*) as count FROM outbox_sales WHERE status = ?`,
            [status]
        );
        return result ? result.count : 0;
    } catch (error) {
        console.error(`[Outbox] Failed to count items by status ${status}:`, error);
        return 0;
    }
}

export async function countPending() {
    try {
        const storage = await getStorage();
        const result = await storage.get(
            `SELECT COUNT(*) as count FROM outbox_sales WHERE status != 'sent'`
        );
        return result ? result.count : 0;
    } catch (error) {
        console.error('[Outbox] Failed to count pending items:', error);
        return 0;
    }
}

export async function hasPendingSync() {
    return (await countPending()) > 0;
}

export async function getAllOutboxItems() {
    try {
        const storage = await getStorage();
        const rows = await storage.query(
            `SELECT * FROM outbox_sales ORDER BY date DESC`
        );
        return rows.map(mapRowToItem);
    } catch (error) {
        console.error('[Outbox] Failed to get all items:', error);
        return [];
    }
}

/**
 * @param {String} saleId
 * @param {String} [storeDb]
 */
export async function getOutboxItem(saleId, storeDb) {
    try {
        const storage = await getStorage();
        const db = normalizeStoreDb(storeDb);
        const row = db
            ? await storage.get(
                `SELECT * FROM outbox_sales WHERE sale_id = ? AND store_db = ?`,
                [saleId, db]
            )
            : await storage.get(
                `SELECT * FROM outbox_sales WHERE sale_id = ?`,
                [saleId]
            );
        return row ? mapRowToItem(row) : undefined;
    } catch (error) {
        console.error(`[Outbox] Failed to get item ${saleId}:`, error);
        return undefined;
    }
}

/**
 * @param {String} saleId
 * @param {String} [storeDb]
 */
export async function deleteOutboxItem(saleId, storeDb) {
    try {
        const storage = await getStorage();
        const db = normalizeStoreDb(storeDb);
        const row = db
            ? await storage.get(
                `SELECT * FROM outbox_sales WHERE sale_id = ? AND store_db = ?`,
                [saleId, db]
            )
            : await storage.get(
                `SELECT * FROM outbox_sales WHERE sale_id = ?`,
                [saleId]
            );

        // Unsynced offline sales must never be deletable - only synced
        // ('sent') rows may be removed (defense-in-depth for the UI guard).
        if (row && row.status !== 'sent') {
            console.warn(
                `[Outbox] Blocked delete of ${saleId} (status: ${row.status}) - only synced sales can be deleted`
            );
            return false;
        }

        if (db) {
            await storage.run(
                `DELETE FROM outbox_sales WHERE sale_id = ? AND store_db = ?`,
                [saleId, db]
            );
        } else {
            await storage.run(
                `DELETE FROM outbox_sales WHERE sale_id = ?`,
                [saleId]
            );
        }
        console.log(`[Outbox] Deleted item: ${saleId}`);
        return true;
    } catch (error) {
        console.error(`[Outbox] Failed to delete item ${saleId}:`, error);
        return false;
    }
}

export async function cleanupSentItems(daysOld = 30) {
    try {
        const storage = await getStorage();
        const threshold = new Date();
        threshold.setDate(threshold.getDate() - daysOld);

        const result = await storage.run(
            `DELETE FROM outbox_sales
             WHERE status = 'sent' AND date < ?`,
            [threshold.toISOString()]
        );

        const deletedCount = result.changes || 0;
        console.log(`[Outbox] Cleaned up ${deletedCount} sent items older than ${daysOld} days`);
        return deletedCount;
    } catch (error) {
        console.error('[Outbox] Failed to cleanup sent items:', error);
        return 0;
    }
}

/**
 * @param {String} saleId
 * @param {String} [storeDb]
 */
export async function forceRetrySaleId(saleId, storeDb) {
    const storage = await getStorage();
    const db = normalizeStoreDb(storeDb);
    const row = db
        ? await storage.get(`SELECT * FROM outbox_sales WHERE sale_id = ? AND store_db = ?`, [saleId, db])
        : await storage.get(`SELECT * FROM outbox_sales WHERE sale_id = ?`, [saleId]);
    if (!row) throw new Error(`Outbox item ${saleId} not found`);

    const resolvedStore = normalizeStoreDb(storeDb || row.store_db);

    const clientMeta = row.client_meta
        ? (typeof row.client_meta === 'string' ? JSON.parse(row.client_meta) : row.client_meta)
        : {};

    const { generateUUID } = await import('../utils/uuid');

    const payload = typeof row.payload === 'string' ? JSON.parse(row.payload) : row.payload;
    const oldLocalId = payload.localId || null;
    const newLocalId = generateUUID();
    payload.localId = newLocalId;

    const forceCount = Number(clientMeta.forceLocalIdCount || 0) + 1;
    const updatedMeta = {
        ...clientMeta,
        forceLocalIdAt: new Date().toISOString(),
        forceLocalIdCount: forceCount,
        previousLocalId: oldLocalId
    };

    if (resolvedStore) {
        await storage.run(
            `UPDATE outbox_sales SET payload = ?, client_meta = ?, status = 'pending', attempts = 0, upload_response = NULL, upload_date = NULL WHERE sale_id = ? AND store_db = ?`,
            [JSON.stringify(payload), JSON.stringify(updatedMeta), saleId, resolvedStore]
        );
    } else {
        await storage.run(
            `UPDATE outbox_sales SET payload = ?, client_meta = ?, status = 'pending', attempts = 0, upload_response = NULL, upload_date = NULL WHERE sale_id = ?`,
            [JSON.stringify(payload), JSON.stringify(updatedMeta), saleId]
        );
    }

    console.log(`[Outbox] Force retry (#${forceCount}) localId: ${oldLocalId} -> ${newLocalId} for sale ${saleId}`);
    return { oldLocalId, newLocalId };
}

export default {
    enqueueSale,
    enqueueSaleWithStockMutation,
    getPending,
    markSending,
    markSent,
    markFailed,
    regenerateLocalId,
    forceRetrySaleId,
    incrementRetries,
    countByStatus,
    countPending,
    hasPendingSync,
    getAllOutboxItems,
    getOutboxItem,
    deleteOutboxItem,
    cleanupSentItems
};
